import { cn } from "./classNames-BzrkDnOD.js";
import { Tooltip } from "./utils-BiRxYJqk.js";
import { useArgoDesign } from "./context-U293r3v-.js";
import { AlertTriangle, ChevronDown, CircleCheck, Info } from "./icons-DcNsvqse.js";
import { Collapsible } from "./Collapsible-BQXWEOBC.js";
import { useThemeAttributes } from "./useThemeAttributes-B199Regy.js";
import { useState } from "react";
import { Slot } from "@radix-ui/react-slot";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/Alert/AlertAction.tsx
const AlertAction = ({ ref, className, asChild = false, ...props }) => {
	return /* @__PURE__ */ jsx(asChild ? Slot : "div", {
		...props,
		ref,
		className: cn("tw:col-start-3", "tw:row-start-1", "tw:self-start", "tw:flex", "tw:gap-2", className)
	});
};
//#endregion
//#region src/components/Alert/AlertCollapsibleIcon.tsx
const AlertCollapsibleIcon = ({ open }) => /* @__PURE__ */ jsx(Tooltip, {
	placement: "top",
	tooltip: open ? "Collapse alert" : "Expand alert",
	triggerAsChild: true,
	children: /* @__PURE__ */ jsx(ChevronDown, { className: cn("tw:mt-px", "tw:size-5", "tw:text-neutral-400", "tw:group-data-[state=open]/alert:rotate-180", "tw:group-data-[variant=info]/alert:group-data-[tint=legacy]/alert:group-hover:text-blue-600", "tw:group-data-[variant=info]/alert:group-data-[tint=token]/alert:group-hover:text-info", "tw:group-data-[variant=warning]/alert:group-data-[tint=legacy]/alert:group-hover:text-warning-600", "tw:group-data-[variant=warning]/alert:group-data-[tint=token]/alert:group-hover:text-warning", "tw:group-data-[variant=error]/alert:group-data-[tint=legacy]/alert:group-hover:text-error-600", "tw:group-data-[variant=error]/alert:group-data-[tint=token]/alert:group-hover:text-destructive", "tw:group-data-[variant=success]/alert:group-data-[tint=legacy]/alert:group-hover:text-green-600", "tw:group-data-[variant=success]/alert:group-data-[tint=token]/alert:group-hover:text-success") })
});
//#endregion
//#region src/components/Alert/AlertDescription.tsx
const AlertDescription = ({ ref, children, className, ...rest }) => /* @__PURE__ */ jsx("div", {
	...rest,
	ref,
	className: cn("tw:col-start-2", "tw:group-has-[[data-slot=alert-title]]/alert:col-end-[-1]", "tw:group-has-[[data-slot=alert-title]]/alert:before:block", "tw:group-has-[[data-slot=alert-title]]/alert:before:h-4", "tw:group-has-[[data-slot=alert-title]]/alert:before:content-['']", "tw:wrap-anywhere", "tw:[&_svg.lucide:not([class*='tw:size-'])]:size-4", "tw:[&_svg]:shrink-0", className),
	children
});
//#endregion
//#region src/components/Alert/AlertIcon.tsx
const VARIANT_ICONS = {
	info: /* @__PURE__ */ jsx(Info, {}),
	warning: /* @__PURE__ */ jsx(AlertTriangle, {}),
	error: /* @__PURE__ */ jsx(AlertTriangle, {}),
	success: /* @__PURE__ */ jsx(CircleCheck, {})
};
const AlertIcon = ({ variant, icon, className, ...rest }) => /* @__PURE__ */ jsx("span", {
	...rest,
	"data-slot": "alert-icon",
	className: cn("tw:inline-flex", "tw:items-center", "tw:justify-center", "tw:self-start", "tw:p-0.5", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-4", "tw:group-data-[variant=info]/alert:group-data-[tint=legacy]/alert:text-blue-500", "tw:group-data-[variant=info]/alert:group-data-[tint=token]/alert:text-info", "tw:group-data-[variant=warning]/alert:group-data-[tint=legacy]/alert:text-warning-500", "tw:group-data-[variant=warning]/alert:group-data-[tint=token]/alert:text-warning", "tw:group-data-[variant=error]/alert:group-data-[tint=legacy]/alert:text-error-500", "tw:group-data-[variant=error]/alert:group-data-[tint=token]/alert:text-destructive", "tw:group-data-[variant=success]/alert:group-data-[tint=legacy]/alert:text-green-500", "tw:group-data-[variant=success]/alert:group-data-[tint=token]/alert:text-success", className),
	children: icon ?? VARIANT_ICONS[variant]
});
//#endregion
//#region src/components/Alert/AlertRoot.tsx
const AlertRoot = ({ ref, children, className, variant, ...rest }) => {
	const renderArgo = useArgoDesign();
	const { scheme } = useThemeAttributes();
	const legacyTint = !renderArgo && scheme !== "dark";
	return /* @__PURE__ */ jsx("div", {
		...rest,
		ref,
		"data-variant": variant,
		"data-argo": renderArgo,
		"data-tint": legacyTint ? "legacy" : "token",
		className: cn("tw:group/alert", "tw:grid", "tw:grid-cols-[0_1fr_auto]", "tw:has-[[data-slot=alert-icon]]:grid-cols-[auto_1fr_auto]", "tw:has-[[data-slot=alert-icon]]:gap-x-2", "tw:text-sm", "tw:data-[tint=legacy]:text-neutral-800", "tw:data-[tint=token]:text-foreground", "tw:text-left", "tw:p-4", "tw:rounded", "tw:data-[argo=true]:rounded-[var(--radius-lg,0.25rem)]", "tw:border", "tw:border-solid", "tw:data-[variant=info]:data-[tint=legacy]:bg-blue-100", "tw:data-[variant=info]:data-[tint=legacy]:border-blue-200", "tw:data-[variant=info]:data-[tint=token]:bg-info/10", "tw:data-[variant=info]:data-[tint=token]:border-info/25", "tw:data-[variant=warning]:data-[tint=legacy]:bg-warning-100", "tw:data-[variant=warning]:data-[tint=legacy]:border-warning-300", "tw:data-[variant=warning]:data-[tint=token]:bg-warning/10", "tw:data-[variant=warning]:data-[tint=token]:border-warning/25", "tw:data-[variant=error]:data-[tint=legacy]:bg-error-100", "tw:data-[variant=error]:data-[tint=legacy]:border-error-200", "tw:data-[variant=error]:data-[tint=token]:bg-destructive/10", "tw:data-[variant=error]:data-[tint=token]:border-destructive/25", "tw:data-[variant=success]:data-[tint=legacy]:bg-green-100", "tw:data-[variant=success]:data-[tint=legacy]:border-green-200", "tw:data-[variant=success]:data-[tint=token]:bg-success/10", "tw:data-[variant=success]:data-[tint=token]:border-success/25", className),
		children
	});
};
//#endregion
//#region src/components/Alert/AlertTitle.tsx
const AlertTitle = ({ ref, className, ...props }) => /* @__PURE__ */ jsx("div", {
	...props,
	ref,
	"data-slot": "alert-title",
	className: cn("tw:group/alert-title", "tw:flex", "tw:col-start-2", "tw:font-medium", "tw:items-center", className)
});
//#endregion
//#region src/components/Alert/Alert.tsx
/**
* Persistent inline banner for contextual messages tied to the page layout.
*
* **When to use:** in-page messages; `mode="collapsible"` hides long error details.
* **When NOT to use:** transient feedback (`toast()`), field errors (`FormGroup state="error"`),
* confirmations (`Popconfirm`).
*
* @see RECIPES.md#alert-with-action
*/
const Alert = ({ title, variant = "info", children, hideIcon = false, icon, className, actions, mode = "default", defaultOpen = true, open: openProp, onOpenChange: onOpenChangeProp, ...rest }) => {
	const isControlled = openProp !== void 0;
	const [innerOpen, setInnerOpen] = useState(defaultOpen);
	const open = isControlled ? openProp : innerOpen;
	const handleOpenChange = (nextOpen) => {
		if (!isControlled) setInnerOpen(nextOpen);
		onOpenChangeProp?.(nextOpen);
	};
	if (mode === "collapsible" && !!title && !!children && !actions) return /* @__PURE__ */ jsx(Collapsible, {
		open,
		onOpenChange: handleOpenChange,
		asChild: true,
		children: /* @__PURE__ */ jsxs(AlertRoot, {
			...rest,
			variant,
			className,
			children: [/* @__PURE__ */ jsxs(Collapsible.Trigger, {
				className: cn("tailwindcss-preflight", "tw:group", "tw:col-span-full", "tw:grid", "tw:grid-cols-subgrid", "tw:rounded-sm", "tw:focus-visible:outline", "tw:focus-visible:outline-2", "tw:focus-visible:outline-ring"),
				children: [
					!hideIcon && /* @__PURE__ */ jsx(AlertIcon, {
						variant,
						icon
					}),
					/* @__PURE__ */ jsx(AlertTitle, { children: title }),
					/* @__PURE__ */ jsx(AlertCollapsibleIcon, { open })
				]
			}), /* @__PURE__ */ jsx(Collapsible.Content, {
				asChild: true,
				children: /* @__PURE__ */ jsx(AlertDescription, { children })
			})]
		})
	});
	return /* @__PURE__ */ jsxs(AlertRoot, {
		...rest,
		variant,
		className,
		children: [
			!hideIcon && /* @__PURE__ */ jsx(AlertIcon, {
				variant,
				icon
			}),
			title && /* @__PURE__ */ jsx(AlertTitle, { children: title }),
			children && /* @__PURE__ */ jsx(AlertDescription, { children }),
			actions && /* @__PURE__ */ jsx(AlertAction, { children: actions })
		]
	});
};
//#endregion
export { Alert };

//# sourceMappingURL=Alert-DyWKn002.js.map